import { useToolboxQuery } from "@/api/toolbox";
import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import {
  useAppNavigate,
  usePaneLocation,
  usePaneParams,
} from "@/hooks/pane-routing";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { resolveGithubEntityRef } from "@/pages/canvas/utils/genericEntityPane";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import { entityWithEnrichedOpenTabUrl } from "@/panes/shared/entityWithEnrichedOpenTabUrl";
import type { GithubRepo } from "@cohesive/api-entities";
import {
  type Entity,
  IntegrationIcon,
  Markdown,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import { File, Folder, GitFork, Lock, Star, X, XCircle } from "lucide-react";
import { useCallback, useMemo } from "react";

type RepoContentItem = {
  name: string;
  path: string;
  type: string;
  size: number;
  html_url: string;
};

type RepoReadme = {
  name?: string;
  path?: string;
  content: string;
  html_url?: string;
};

// github-repository-get returns the typed entity envelope; the repo fields are
// nested under `.data`.
type RepoEntityResponse = { data?: GithubRepo };

export function GitHubRepoDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  usePaneParams(ROUTES.GITHUB.REPO_DETAIL);
  const paneLocation = usePaneLocation("rightPane");
  const entity = paneLocation?.state?.entity as Entity | undefined;
  const data = (entity?.data ?? {}) as Record<string, unknown>;

  // Repos arrive as generic entities (owner/repo live in the URL) or as typed
  // github_repo entities (owner/repo in full_name). resolveGithubEntityRef
  // handles both.
  const ref = resolveGithubEntityRef(data);
  const owner = ref?.owner;
  const repoName = ref?.repo;
  const connectionId = useIntegrationConnection("github")?.id;
  const canFetch = !!connectionId && !!owner && !!repoName;

  const fetchInput = { owner, repo: repoName };

  const { data: repoResp, isLoading: repoLoading } =
    useToolboxQuery<RepoEntityResponse>(
      canFetch
        ? {
            integrationConnectionId: connectionId,
            actionName: "github-repository-get",
            actionInput: fetchInput,
          }
        : null,
      { staleTime: 5 * 60 * 1000 },
    );

  const { data: contents, isLoading: contentsLoading } = useToolboxQuery<
    RepoContentItem[]
  >(
    canFetch
      ? {
          integrationConnectionId: connectionId,
          actionName: "github-repository-contents",
          actionInput: fetchInput,
        }
      : null,
    { staleTime: 5 * 60 * 1000 },
  );

  const { data: readme, isLoading: readmeLoading } =
    useToolboxQuery<RepoReadme>(
      canFetch
        ? {
            integrationConnectionId: connectionId,
            actionName: "github-repository-readme",
            actionInput: fetchInput,
          }
        : null,
      { staleTime: 5 * 60 * 1000 },
    );

  // Prefer the fetched repo; fall back to whatever the generic entity carried so
  // the header renders immediately while the fetch is in flight.
  const repo = repoResp?.data;
  const fullName =
    repo?.full_name ||
    (typeof data.full_name === "string" ? data.full_name : undefined) ||
    (owner && repoName ? `${owner}/${repoName}` : undefined) ||
    (typeof data.title === "string" ? data.title : "Repository");
  const description =
    repo?.description ||
    (typeof data.description === "string" ? data.description : undefined);

  const entityForHeaderActions = useMemo(
    () =>
      entity
        ? entityWithEnrichedOpenTabUrl(entity, {
            url: repo?.url,
            web_link: repo?.web_link,
          })
        : undefined,
    [entity, repo?.url, repo?.web_link],
  );

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  if (!entity) {
    return (
      <div className="flex flex-col h-full bg-background items-center justify-center">
        <XCircle className="h-10 w-10 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground">Repository not found</p>
      </div>
    );
  }

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />
          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <IntegrationIcon
              platform="github"
              name="GitHub"
              className="h-4 w-4 shrink-0"
            />
            <span className="text-sm font-medium text-foreground truncate leading-none">
              {fullName}
            </span>
          </div>
          <TooltipProvider>
            {entityForHeaderActions && (
              <EntityPaneHeaderObjectActions
                entity={entityForHeaderActions}
                name={fullName}
              />
            )}
            <PaneExpandButton />
            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent>
        <div className="space-y-5">
          {/* Title + metadata */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold break-all">{fullName}</h2>
              {repo?.is_private && (
                <Badge variant="outline" className="text-xs gap-1">
                  <Lock className="h-3 w-3" />
                  Private
                </Badge>
              )}
            </div>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {repo?.language && (
                <span className="flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-lc-active" />
                  {repo.language}
                </span>
              )}
              {repo?.stars_count !== undefined && (
                <span className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5" />
                  {repo.stars_count}
                </span>
              )}
              {repo?.forks_count !== undefined && (
                <span className="flex items-center gap-1">
                  <GitFork className="h-3.5 w-3.5" />
                  {repo.forks_count}
                </span>
              )}
              {repo?.updated_at && (
                <span>
                  Updated{" "}
                  {formatDistanceToNow(new Date(repo.updated_at), {
                    addSuffix: true,
                  })}
                </span>
              )}
            </div>
            {repo?.topics && repo.topics.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {repo.topics.map((topic) => (
                  <Badge key={topic} variant="secondary" className="text-xs">
                    {topic}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Top-level files */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Files</h3>
            <div className="rounded-md border divide-y">
              {contentsLoading ? (
                <div className="p-3 text-sm text-muted-foreground">
                  Loading files…
                </div>
              ) : contents && contents.length > 0 ? (
                contents.map((item) => (
                  <a
                    key={item.path}
                    href={item.html_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/50 transition-colors"
                  >
                    {item.type === "dir" ? (
                      <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : (
                      <File className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="truncate">{item.name}</span>
                  </a>
                ))
              ) : (
                <div className="p-3 text-sm text-muted-foreground italic">
                  {canFetch ? "No files found" : "Connect GitHub to view files"}
                </div>
              )}
            </div>
          </div>

          {/* README */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">README</h3>
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {readmeLoading || repoLoading ? (
                <p className="text-muted-foreground">Loading README…</p>
              ) : readme?.content ? (
                <Markdown>{readme.content}</Markdown>
              ) : (
                <p className="text-muted-foreground italic">No README found</p>
              )}
            </div>
          </div>
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
